/**
 * Audio Utilities - Shared audio processing functions for visualizers
 *
 * These utilities provide common audio data processing used by all visualizers:
 * - Logarithmic frequency mapping (more detail in lower frequencies)
 * - Exponential amplitude smoothing
 * - Color interpolation
 */

/**
 * Maps frequency data to visualization elements using logarithmic distribution.
 * Provides more detail in lower frequencies where human hearing is more sensitive.
 *
 * @param {Uint8Array} frequencyData - Raw frequency data from AnalyserNode (0-255 values)
 * @param {number} elementCount - Number of visualization elements to map to
 * @param {Array|null} buffer - Optional pre-allocated buffer to reuse (avoids GC)
 * @returns {Array<number>} Normalized amplitudes (0-1) for each element
 */
export function mapFrequencyData(frequencyData, elementCount, buffer = null) {
  const result = buffer || new Array(elementCount);
  const dataLength = frequencyData.length;

  for (let i = 0; i < elementCount; i++) {
    // Logarithmic distribution - more detail in low frequencies
    const lowFreq = Math.floor(Math.pow(dataLength, i / elementCount));
    const highFreq = Math.floor(Math.pow(dataLength, (i + 1) / elementCount));

    let sum = 0;
    let count = 0;
    for (let j = lowFreq; j < highFreq && j < dataLength; j++) {
      sum += frequencyData[j];
      count++;
    }

    // Normalize to 0-1 range
    result[i] = count > 0 ? (sum / count) / 255 : 0;
  }

  return result;
}

/**
 * Applies exponential smoothing to amplitude values.
 * Creates smooth transitions between frames, reducing jitter.
 *
 * Formula: smoothed[i] = previous[i] * factor + current[i] * (1 - factor)
 *
 * @param {Array<number>} current - Current frame's amplitude values
 * @param {Array<number>} previous - Previous frame's smoothed values (modified in place)
 * @param {number} smoothingFactor - Smoothing factor (0-1). Higher = smoother/slower response
 * @returns {Array<number>} The previous array (now containing smoothed values)
 */
export function smoothAmplitudes(current, previous, smoothingFactor) {
  const oneMinusFactor = 1 - smoothingFactor;
  for (let i = 0; i < current.length; i++) {
    previous[i] = previous[i] * smoothingFactor + current[i] * oneMinusFactor;
  }
  return previous;
}

/**
 * Linear interpolation between two hex colors.
 *
 * @param {string} colorStart - Start color in hex format (#RRGGBB)
 * @param {string} colorEnd - End color in hex format (#RRGGBB)
 * @param {number} t - Interpolation factor (0-1)
 * @returns {string} Interpolated color in rgb() format
 */
export function lerpColor(colorStart, colorEnd, t) {
  // Parse hex colors
  const r1 = parseInt(colorStart.slice(1, 3), 16);
  const g1 = parseInt(colorStart.slice(3, 5), 16);
  const b1 = parseInt(colorStart.slice(5, 7), 16);

  const r2 = parseInt(colorEnd.slice(1, 3), 16);
  const g2 = parseInt(colorEnd.slice(3, 5), 16);
  const b2 = parseInt(colorEnd.slice(5, 7), 16);

  // Linear interpolation
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);

  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Linear interpolation between two hex colors, returning hex format.
 *
 * @param {string} colorStart - Start color in hex format (#RRGGBB)
 * @param {string} colorEnd - End color in hex format (#RRGGBB)
 * @param {number} t - Interpolation factor (0-1)
 * @returns {string} Interpolated color in hex format (#RRGGBB)
 */
export function lerpColorHex(colorStart, colorEnd, t) {
  const r1 = parseInt(colorStart.slice(1, 3), 16);
  const g1 = parseInt(colorStart.slice(3, 5), 16);
  const b1 = parseInt(colorStart.slice(5, 7), 16);

  const r2 = parseInt(colorEnd.slice(1, 3), 16);
  const g2 = parseInt(colorEnd.slice(3, 5), 16);
  const b2 = parseInt(colorEnd.slice(5, 7), 16);

  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Extracts default values from a getConfigUI() definition.
 * Used by base visualizer classes to merge UI defaults into config.
 *
 * @param {Array} configUI - Array of config items from getConfigUI()
 * @returns {object} Object mapping config IDs to their default values
 */
export function extractConfigDefaults(configUI) {
  const defaults = {};
  for (const item of configUI) {
    if (item.id && item.default !== undefined) {
      // Apply scale if present (e.g., smoothing 85 with scale 0.01 = 0.85)
      defaults[item.id] = item.scale ? item.default * item.scale : item.default;
    }
  }
  return defaults;
}
